import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  makeStyles,
  MenuItem,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from '@material-ui/core';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import { useTranslation } from 'react-i18next';
import { ASSIGNABLE_PROJECT_ROLES } from '../../roles';
import { memberDisplayName, SELF_MEMBER_ID } from './memberLabels';
import { ProjectMember, ProjectRole } from './types';
import { isValidProjectMemberEmail } from './useProjectStore';

const useStyles = makeStyles((theme: Theme) => ({
  dialogField: {
    marginTop: theme.spacing(2),
  },
  memberBuilder: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.default,
  },
  memberList: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  memberRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 0),
  },
  memberInfo: {
    minWidth: 0,
  },
  memberActions: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  roleSelect: {
    minWidth: 160,
  },
  subtle: {
    color: theme.palette.text.secondary,
  },
}));

type MemberBuilderProps = {
  members: ProjectMember[];
  onMembersChange: (members: ProjectMember[]) => void;
  /**
   * Resolves whether an email belongs to the current org. Undefined when
   * there's no project to check against yet (e.g. this is the user's first
   * project) — callers should treat that the same as "unknown", not "not in
   * org", since the org-restricted dialog used after a project exists can't
   * be reused here (it needs a project id this draft doesn't have yet).
   */
  checkOrgMembership?: (email: string) => Promise<boolean | undefined>;
};

export function MemberBuilder(props: MemberBuilderProps) {
  const classes = useStyles();
  const { t } = useTranslation('projects');
  const [memberName, setMemberName] = useState('');
  const [memberEmail, setMemberEmail] = useState('');
  const [memberRole, setMemberRole] = useState<ProjectRole>('Collaborator');
  const [orgStatus, setOrgStatus] = useState<
    Record<string, boolean | undefined>
  >({});

  const trimmedName = memberName.trim();
  const trimmedEmail = memberEmail.trim();
  const canAddMember =
    Boolean(trimmedName) && isValidProjectMemberEmail(trimmedEmail);

  const addMember = () => {
    if (!canAddMember) {
      return;
    }

    const newMember: ProjectMember = {
      id: `${trimmedEmail.toLowerCase()}-${Date.now()}`,
      name: trimmedName,
      email: trimmedEmail,
      role: memberRole,
    };

    props.onMembersChange([...props.members, newMember]);
    setMemberName('');
    setMemberEmail('');
    setMemberRole('Collaborator');

    // Best-effort: leaves the member unmarked (not flagged as "not in org")
    // on error or when there's nothing to check against yet, since a false
    // "not in org" would be misleading.
    props.checkOrgMembership?.(newMember.email).then(isInOrg => {
      setOrgStatus(current => ({ ...current, [newMember.id]: isInOrg }));
    });
  };

  const changeMemberRole = (member: ProjectMember, role: ProjectRole) => {
    props.onMembersChange(
      props.members.map(currentMember =>
        currentMember.id === member.id
          ? { ...currentMember, role }
          : currentMember,
      ),
    );
  };

  return (
    <div className={classes.memberBuilder}>
      <Typography variant="subtitle1">
        {t('createDialog.assignMembersTitle')}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {t('createDialog.assignMembersDescription')}
      </Typography>
      <Grid container spacing={2} className={classes.dialogField}>
        <Grid item sm={6} xs={12}>
          <TextField
            fullWidth
            label={t('fields.name')}
            value={memberName}
            onChange={event => setMemberName(event.target.value)}
            variant="outlined"
          />
        </Grid>
        <Grid item sm={6} xs={12}>
          <TextField
            fullWidth
            error={
              Boolean(trimmedEmail) && !isValidProjectMemberEmail(trimmedEmail)
            }
            helperText={
              Boolean(trimmedEmail) && !isValidProjectMemberEmail(trimmedEmail)
                ? t('fields.emailInvalid')
                : undefined
            }
            label={t('fields.email')}
            value={memberEmail}
            onChange={event => setMemberEmail(event.target.value)}
            variant="outlined"
          />
        </Grid>
        <Grid item sm={6} xs={12}>
          <TextField
            select
            fullWidth
            label={t('fields.role')}
            value={memberRole}
            onChange={event => setMemberRole(event.target.value as ProjectRole)}
            variant="outlined"
          >
            {ASSIGNABLE_PROJECT_ROLES.map(role => (
              <MenuItem key={role} value={role}>
                {t(`roles.${role}`)}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>
      <Box mt={2}>
        <Button
          startIcon={<PersonAddIcon />}
          variant="outlined"
          color="primary"
          disabled={!canAddMember}
          onClick={addMember}
        >
          {t('actions.addMember')}
        </Button>
      </Box>

      <div className={classes.memberList}>
        {props.members.map((member, index) => (
          <div key={member.id}>
            {index > 0 && <Divider />}
            <div className={classes.memberRow}>
              <div className={classes.memberInfo}>
                <div className={classes.nameRow}>
                  <Typography variant="body1">
                    {memberDisplayName(member, t('members.selfName'))}
                  </Typography>
                  {orgStatus[member.id] === false && (
                    <Tooltip title={t('members.notInOrg')}>
                      <ErrorOutlineIcon
                        fontSize="small"
                        color="error"
                        aria-label={t('members.notInOrg')}
                      />
                    </Tooltip>
                  )}
                </div>
                <Typography variant="body2" className={classes.subtle}>
                  {member.email}
                </Typography>
              </div>
              <div className={classes.memberActions}>
                {member.id === SELF_MEMBER_ID ? (
                  <Chip size="small" label={t(`roles.${member.role}`)} />
                ) : (
                  <TextField
                    select
                    size="small"
                    variant="outlined"
                    className={classes.roleSelect}
                    value={member.role}
                    onChange={event =>
                      changeMemberRole(
                        member,
                        event.target.value as ProjectRole,
                      )
                    }
                    SelectProps={{
                      SelectDisplayProps: {
                        'aria-label': t('members.changeRoleFor', {
                          name: memberDisplayName(
                            member,
                            t('members.selfName'),
                          ),
                        }),
                      },
                    }}
                  >
                    {ASSIGNABLE_PROJECT_ROLES.map(role => (
                      <MenuItem key={role} value={role}>
                        {t(`roles.${role}`)}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                {member.id !== SELF_MEMBER_ID && (
                  <Tooltip title={t('actions.removeMember')}>
                    <IconButton
                      size="small"
                      onClick={() =>
                        props.onMembersChange(
                          props.members.filter(
                            currentMember => currentMember.id !== member.id,
                          ),
                        )
                      }
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
